import LoadScript from "../utils/LoadScript";
import {GlobalVariables} from "../GlobalVariables";

export default class VideoPlayer {

	static init() {
		this.initModal();
		this.buildEverythingElse();
	}

	static buildEverythingElse() {
		this.containers = document.querySelectorAll('.video-player-container');

		// Utilisé pour lancer la vidéo une fois la lib chargée
		window.onYouTubeIframeAPIReady = function () {
			document.dispatchEvent(new CustomEvent('onYouTubeIframeAPIReady', {}));
		};

		this.containers.forEach(container => {

			// Récupération de la div pour le player + détermination youtube / viméo
			container.player = container.querySelector(".player");
			if (container.player.classList.contains("youtube")) {
				container.isYoutube = true;
			} else if (container.player.classList.contains("vimeo")) {
				container.isVimeo = true;
			}

			container.isModal = (container.player.dataset.modal === "true");

			// Plugin RGPD actif
			if (document.body.classList.contains("cc-active")) {
				// Plugin RGPD chargé
				if (this.isGDPRinitialized()) {
					this.initIfPossible(container);
				} else {
					// Plugin RGPD non chargé
					setTimeout(() => {
						// Plugin RGPD chargé
						if (typeof Cc === "function") {
							this.initIfPossible(container);
						} else {
							// Plugin RGPD none chargé, on laisser tomber
							this.initPlayerImage(container);
						}
					}, 500);
				}
			} else {
				// Pas de plugin RGPD
				this.initPlayerImage(container);
			}

			container.addEventListener('click', (e) => {
				// Vérification, si le clic est sur le message RGPD -> on ne lance pas la vidéo
				if (e.target.classList.contains("cookie-missing-container") || e.target.closest(".cookie-missing-container")) {
					return;
				}
				container.classList.add("playing");
				this.play(container);
			});
		});

		// À l'acceptation des cookies fonctionnels, on charge les images si nécéssaire
		document.addEventListener("GDPR.functionalGranted", () => {
			this.containers.forEach(container => {
				if (!container.player) {
					return;
				}
				container.classList.remove("cookie-missing");
				this.initPlayerImage(container);
			});
		});

		document.addEventListener("escape.pressed", () => {
			this.closeModal();
		});
	}

	static initIfPossible(container) {
		if (this.isFunctionalCookieGranted()) {
			// Les cookies fonctionnels sont acceptés
			this.initPlayerImage(container);
		} else {
			// Les cookies fonctionnels sont rejetés, affichage du message
			container.classList.add("cookie-missing");
		}
	}

	static isGDPRinitialized() {
		return window.gdpr?.initialized === 1;
	}

	static isFunctionalCookieGranted() {
		return window.gdpr?.functional?.granted === 1 || (ccArgs.tech && !ccArgs.functional);
	}

	static play(container) {

		if (container.isModal) {
			this.modal.originalContainer = container;
			this.modal.insertAdjacentHTML("afterbegin", `<div class="player"></div>`);
			this.modal.player = this.modal.querySelector(".player");
			this.modal.player.dataset.videoId = container.player.dataset.videoId;
			this.modal.player.dataset.videoContainerId = "modal" + container.player.dataset.videoId;
			this.modal.player.id = this.modal.player.dataset.videoContainerId;
			this.modal.isYoutube = container.isYoutube;
			this.modal.isVimeo = container.isVimeo;
			this.modal.isModal = true;


			this.modal.classList.add("show");
			GlobalVariables.lenis?.stop();

			if (this.modal.isYoutube) {
				this.playYoutube(this.modal);
			} else if (this.modal.isVimeo) {
				this.playVimeo(this.modal);
			}
		} else {
			if (container.isYoutube) {
				this.playYoutube(container);
			} else if (container.isVimeo) {
				this.playVimeo(container);
			}
		}
	}

	static playYoutube(container) {
		LoadScript.load('https://www.youtube.com/iframe_api');
		if (this.youtubeApiLoaded) {
			// API déjà chargée, on lance la lecture
			this.initYoutubePlayer(container);
		} else {
			// On attend que l'api soit chargée, via le listener posé en début de code
			document.addEventListener('onYouTubeIframeAPIReady', () => {
				this.youtubeApiLoaded = true;
				this.initYoutubePlayer(container);
			});
		}
	}

	static initYoutubePlayer(container) {
		if (container.player.youtubeVideo && !container.isModal) {
			// Player youtube déjà initialisé
			container.player.youtubeVideo.playVideo();
		} else {
			const videoID = container.player.dataset.videoId;
			const videoContainerID = container.player.dataset.videoContainerId;
			// Création du player youtube
			container.player.youtubeVideo = new YT.Player(videoContainerID ?? videoID, {
				height: '360',
				width: '640',
				videoId: videoID,
				playerVars: {
					autoplay: 1,
					showinfo: 0,
					modestbranding: 1,
					rel: 0,
					enablejsapi: 1
				},
				events: {
					onReady: (event) => event.target.playVideo(),
				}
			});
		}
	}

	static playVimeo(container) {
		LoadScript.load('https://player.vimeo.com/api/player.js', this.initVimeoPlayer.bind(this, container));
	}

	static initVimeoPlayer(container) {

		if (container.player.vimeoVideo && !container.isModal) {
			container.player.vimeoVideo.play();
		} else {
			const videoID = container.player.dataset.videoId;
			const videoContainerID = container.player.dataset.videoContainerId;
			const finalID = videoContainerID ?? videoID;
			if (!finalID) {
				return;
			}
			container.player.vimeoVideo = new Vimeo.Player(videoContainerID ?? videoID, {id: videoID});

			container.player.vimeoVideo.on("pause", () => {
				container.classList.remove('playing');
			});

			container.player.vimeoVideo.on("play", () => {
				container.classList.add('playing');
			});

			container.player.vimeoVideo.play();
		}
	}

	static initPlayerImage(container) {
		if (container.isYoutube) {
			this.initYoutubePlayerImage(container);
		} else if (container.isVimeo) {
			this.initVimeoPlayerImage(container);
		}
	}

	static initYoutubePlayerImage(container) {
		const videoID = container.player.dataset.videoId;

		if (!container.player.querySelector('img')) {
			const img = document.createElement('img');
			img.src = `https://img.youtube.com/vi/${videoID}/hqdefault.jpg`;
			container.player.append(img);
		}
	}

	static initVimeoPlayerImage(container) {
		let url = container.player.dataset.videoUrl;
		const xhttp = new XMLHttpRequest();
		xhttp.onload = function () {
			let poster = xhttp.response.thumbnail_url;
			let img = document.createElement('img');
			img.src = poster;
			container.player.append(img);
		};
		xhttp.responseType = 'json';
		xhttp.open("GET", 'https://vimeo.com/api/oembed.json?url=' + encodeURIComponent(url), true);
		xhttp.send();
	}

	static initModal() {
		this.modal = document.querySelector("#video-player-modal");
		this.modal.player = this.modal.querySelector(".player");
		const close = this.modal.querySelector('.close');

		close.addEventListener("click", () => {
			this.closeModal();
		});

		const that = this;

		this.modal.addEventListener('click', function (e) {
			if (e.target !== this) {
				return;
			}
			that.closeModal();
		});
	}

	static closeModal() {
		if (!this.modal.classList.contains("show")) {
			return;
		}

		GlobalVariables.lenis?.start();
		this.modal.classList.remove('show');
		this.modal.originalContainer.classList.remove("playing");
		if (this.modal.isYoutube && this.modal.player.youtubeVideo && this.modal.player.youtubeVideo.pauseVideo) {
			this.modal.player.youtubeVideo.pauseVideo();
		} else if (this.modal.isVimeo && this.modal.player.vimeoVideo) {
			this.modal.player.vimeoVideo.pause();
		}
		this.modal.querySelector(".player").remove();
	}
}
